<template>
  <div class="listing-detail-root">
    <div id="layout-wrapper" class="ld-layout-wrapper">

      <!-- ========== TOP BAR ========== -->
      <nav class="ld-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center" style="gap:12px;">
            <button class="ld-back" @click="goBack" title="Back"><i class="mdi mdi-arrow-left"></i></button>
            <a href="javascript:void(0)" @click="$router.push('/')">
              <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" />
            </a>
          </div>
          <div class="d-flex align-items-center" style="gap:12px;">
            <a href="javascript:void(0)" @click="$router.push('/marketplace')" class="ld-nav-link">Marketplace</a>
            <a href="javascript:void(0)" @click="$router.push('/forum')" class="ld-nav-link">Community</a>
            <a href="javascript:void(0)" @click="$router.push('/marketplace/search')" class="ld-nav-link">Search</a>
            <a href="javascript:void(0)" @click="$router.push('/sell/create')" class="btn btn-sm btn-outline-warning">
              <i class="mdi mdi-plus me-1"></i> Sell
            </a>
            <a href="javascript:void(0)" v-if="myAvatar || myName" @click="$router.push('/me')" title="My profile">
              <user-avatar :src="myAvatar" :name="myName" size="sm" />
            </a>
          </div>
        </div>
      </nav>

      <!-- ========== LOADING ========== -->
      <div v-if="loading" class="text-center py-5" style="margin-top:80px;">
        <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
        <p class="mt-2 text-muted">Loading listing details...</p>
      </div>

      <!-- ========== NOT FOUND ========== -->
      <div v-else-if="!listing.id" class="text-center py-5" style="margin-top:80px;">
        <i class="mdi mdi-alert-circle-outline" style="font-size:64px;color:var(--fm-m,#A09888);"></i>
        <h4 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Listing Not Found</h4>
        <p class="text-muted">This listing may have been removed or is no longer available.</p>
        <a href="javascript:void(0)" @click="$router.push('/marketplace')" class="btn btn-warning text-dark fw-bold">
          <i class="mdi mdi-store me-1"></i> Browse Marketplace
        </a>
      </div>

      <!-- ========== MAIN CONTENT ========== -->
      <div v-else class="container py-4" style="margin-top:10px;">

        <!-- Breadcrumb -->
        <nav class="mb-3">
          <ol class="breadcrumb ld-breadcrumb">
            <li class="breadcrumb-item"><a href="javascript:void(0)" @click="$router.push('/marketplace')">Marketplace</a></li>
            <li class="breadcrumb-item" v-if="listing.species_name"><a href="javascript:void(0)" @click="$router.push('/marketplace/category/' + (listing.species_slug || ''))">{{ listing.species_name }}</a></li>
            <li class="breadcrumb-item active">{{ truncate(listing.title, 40) }}</li>
          </ol>
        </nav>

        <div class="row">

          <!-- ===== LEFT: PHOTOS & DETAILS ===== -->
          <div class="col-lg-8">

            <!-- Photo Gallery -->
            <div class="ld-gallery mb-4">
              <div class="ld-main-photo">
                <img v-if="activePhoto" :src="activePhoto" :alt="listing.title" class="ld-main-img" />
                <div v-else class="ld-main-placeholder">
                  <i :class="getSpeciesIcon(listing.species_name)" style="font-size:80px;color:rgba(191,161,100,0.25);"></i>
                  <p style="color:var(--fm-m,#A09888);margin-top:8px;font-size:14px;">No photos available</p>
                </div>
                <!-- Photo nav arrows -->
                <button v-if="photos.length > 1" class="ld-photo-nav ld-photo-prev" @click="prevPhoto"><i class="mdi mdi-chevron-left"></i></button>
                <button v-if="photos.length > 1" class="ld-photo-nav ld-photo-next" @click="nextPhoto"><i class="mdi mdi-chevron-right"></i></button>
                <!-- Photo counter -->
                <div v-if="photos.length > 1" class="ld-photo-counter">{{ activePhotoIndex + 1 }} / {{ photos.length }}</div>
                <div v-if="activePhotoTimestamp" class="ld-photo-timestamp" :title="activePhotoTimestampTitle"><i class="mdi mdi-camera-iris me-1"></i>{{ activePhotoTimestamp }}</div>
                <!-- Badges -->
                <div class="ld-photo-badges">
                  <span v-if="listing.animal_id" class="badge" style="background:rgba(80,200,120,.9);color:#fff;"><i class="mdi mdi-barn me-1"></i>Farm Records</span>
                  <span v-if="listing.seller_verified" class="badge bg-success" title="Seller has completed KYC identity verification"><i class="mdi mdi-check-decagram me-1"></i>Seller Verified</span>
                  <span v-if="Number(listing.vet_verified) === 1" class="badge" style="background:rgba(66,133,244,.9);color:#fff;" title="Animal inspected and verified by a vet on Flockr"><i class="mdi mdi-stethoscope me-1"></i>Vet Verified</span>
                  <span v-if="listing.anitrac_id && listing.anitrac_verified_at" class="badge" style="background:rgba(80,200,120,.95);color:#fff;" :title="'ANITRAC tag: ' + listing.anitrac_id + ' — verified against Kenya\'s national livestock traceability database'"><i class="mdi mdi-shield-check me-1"></i>ANITRAC ✓</span>
                  <span v-else-if="listing.anitrac_id" class="badge" style="background:rgba(160,152,136,.6);color:#fff;" :title="'ANITRAC tag entered: ' + listing.anitrac_id + ' — pending verification against the national registry'"><i class="mdi mdi-shield-outline me-1"></i>ANITRAC pending</span>
                  <span v-if="listing.is_featured" class="badge bg-warning text-dark"><i class="mdi mdi-star me-1"></i>Featured</span>
                </div>
              </div>
              <!-- Thumbnails -->
              <div class="ld-thumbnails" v-if="photos.length > 1">
                <div v-for="(photo, pi) in photos" :key="'th-'+pi"
                     class="ld-thumb" :class="{ active: activePhotoIndex === pi }"
                     @click="activePhotoIndex = pi">
                  <img :src="photoUrl(photo.photo_url || photo)" :alt="'Photo ' + (pi+1)" />
                </div>
              </div>
            </div>

            <!-- Listing Title & Quick Info (mobile) -->
            <div class="d-lg-none mb-4">
              <h3 class="fw-bold mb-2" style="color:var(--fm-h,#F5F0E8);">{{ listing.title }}</h3>
              <div class="ld-price-big mb-2">{{ listing.currency || 'KES' }} {{ formatPrice(listing.price) }}
                <span class="ld-price-unit">{{ priceUnitLabel(listing.price_unit) }}</span>
              </div>
              <div class="d-flex align-items-center flex-wrap mb-2" style="gap:6px;">
                <span v-if="listing.seller_verified" class="badge bg-success bg-opacity-25 text-success" title="Seller has completed KYC identity verification"><i class="mdi mdi-check-decagram"></i> Seller Verified</span>
                <span v-if="Number(listing.vet_verified) === 1" class="badge bg-info bg-opacity-25 text-info" title="Animal inspected and verified by a vet on Flockr"><i class="mdi mdi-stethoscope"></i> Vet Verified</span>
                <span v-if="listing.is_featured" class="badge bg-warning bg-opacity-25 text-warning"><i class="mdi mdi-star"></i> Featured</span>
                <span class="badge bg-dark" style="border:1px solid var(--fm-br,#2A2520);" v-if="isProduct && listing.product_category_name">{{ listing.product_category_name }}</span>
                <span class="badge bg-dark" style="border:1px solid var(--fm-br,#2A2520);" v-else-if="listing.listing_type">{{ listing.listing_type }}</span>
                <span v-if="isMeatListing" class="badge" style="background:#7A2E2E;color:#FFD9D9;border:1px solid #A54848;">
                  <i class="mdi mdi-food-steak me-1"></i>Freshly Slaughtered
                </span>
                <span v-if="listing.body_condition_score" class="badge ld-bcs-pill" :class="bodyScoreBadge(listing.body_condition_score)" :title="bodyScoreInterpretation(listing.body_condition_score)">
                  <i class="mdi mdi-heart-pulse me-1"></i>BCS {{ Number(listing.body_condition_score) }}/5
                </span>
              </div>
            </div>

            <!-- Animal / Product Details -->
            <div class="ld-section">
              <h5 class="ld-section-title"><i class="mdi mdi-information me-2"></i>{{ isProduct ? 'Product Details' : 'Animal Details' }}</h5>
              <div class="row g-3">
                <div class="col-6 col-md-4" v-if="isProduct && listing.product_category_name">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Category</div>
                    <div class="ld-detail-value"><i class="mdi mdi-package-variant me-1"></i> {{ listing.product_category_name }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="isProduct && Number(listing.available_quantity) > 0">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Available Quantity</div>
                    <div class="ld-detail-value"><i class="mdi mdi-numeric me-1"></i> {{ listing.available_quantity }} {{ productUnitNoun }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="isProduct && Number(listing.min_order_quantity) > 1">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Minimum Order</div>
                    <div class="ld-detail-value"><i class="mdi mdi-cart-arrow-down me-1"></i> {{ listing.min_order_quantity }} {{ productUnitNoun }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="!isProduct && listing.species_name">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Species</div>
                    <div class="ld-detail-value"><i :class="getSpeciesIcon(listing.species_name)" class="me-1"></i> {{ listing.species_name }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.breed_name">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Breed</div>
                    <div class="ld-detail-value">{{ listing.breed_name }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.sex">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Sex</div>
                    <div class="ld-detail-value"><i class="mdi mdi-gender-male-female me-1"></i> {{ listing.sex }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="!isProduct && (listing.weight_kg || listing.avg_weight_kg)">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">{{ listing.weight_kg ? 'Weight' : 'Avg. Weight' }}</div>
                    <div class="ld-detail-value"><i class="mdi mdi-weight-kilogram me-1"></i> {{ listing.weight_kg || listing.avg_weight_kg }} kg</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.body_condition_score">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Body Condition Score</div>
                    <div class="ld-detail-value" :title="bodyScoreInterpretation(listing.body_condition_score)"><i class="mdi mdi-heart-pulse me-1"></i> {{ Number(listing.body_condition_score) }} / 5 <span class="badge ms-1" :class="bodyScoreBadge(listing.body_condition_score)">{{ bodyScoreLabel(listing.body_condition_score) }}</span></div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.age_text || listing.age_months">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Age</div>
                    <div class="ld-detail-value"><i class="mdi mdi-clock-outline me-1"></i> {{ listing.age_text || listing.age_months + ' months' }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="!isProduct && Number(listing.total_quantity) > 1">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Quantity</div>
                    <div class="ld-detail-value"><i class="mdi mdi-numeric me-1"></i> {{ Number(listing.available_quantity) > 0 ? listing.available_quantity + ' of ' + listing.total_quantity + ' available' : listing.total_quantity }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="!isProduct && listing.listing_type">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Listing Type</div>
                    <div class="ld-detail-value">{{ listing.listing_type === 'batch' ? 'Batch' : 'Individual' }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.county_name">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Location</div>
                    <div class="ld-detail-value"><i class="mdi mdi-map-marker me-1"></i> {{ listing.county_name }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.created_at">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Listed</div>
                    <div class="ld-detail-value"><i class="mdi mdi-calendar me-1"></i> {{ formatDate(listing.created_at) }}</div>
                  </div>
                </div>
                <div class="col-6 col-md-4" v-if="listing.views_count">
                  <div class="ld-detail-item">
                    <div class="ld-detail-label">Views</div>
                    <div class="ld-detail-value"><i class="mdi mdi-eye me-1"></i> {{ listing.views_count }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Butchery workflow — cuts breakdown for Meat (Carcass) listings -->
            <div class="ld-section" v-if="isMeatListing && (listing.cuts || []).length > 0">
              <h5 class="ld-section-title"><i class="mdi mdi-food-steak me-2"></i>Cuts &amp; Pricing</h5>
              <div class="table-responsive">
                <table class="table table-sm mb-2" style="color:var(--fm-h,#F5F0E8);">
                  <thead>
                    <tr style="font-size:12px;color:var(--fm-s,#6B6356);">
                      <th style="border-color:var(--fm-br,#2A2520);">Cut</th>
                      <th style="border-color:var(--fm-br,#2A2520);">Grade</th>
                      <th style="border-color:var(--fm-br,#2A2520);" class="text-end">Weight (kg)</th>
                      <th style="border-color:var(--fm-br,#2A2520);" class="text-end">Price / kg</th>
                      <th style="border-color:var(--fm-br,#2A2520);" class="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="cut in listing.cuts" :key="cut.id" style="font-size:13px;">
                      <td style="border-color:var(--fm-br,#2A2520);">{{ cut.cut_name }}</td>
                      <td style="border-color:var(--fm-br,#2A2520);">
                        <span v-if="cut.quality_grade" class="badge" :style="gradeBadgeStyle(cut.quality_grade)">{{ cut.quality_grade }} — {{ gradeLabel(cut.quality_grade) }}</span>
                        <span v-else style="color:var(--fm-s,#6B6356);">—</span>
                      </td>
                      <td style="border-color:var(--fm-br,#2A2520);" class="text-end">{{ Number(cut.weight_kg) }}</td>
                      <td style="border-color:var(--fm-br,#2A2520);" class="text-end">{{ formatPrice(cut.price_per_kg) }}</td>
                      <td style="border-color:var(--fm-br,#2A2520);" class="text-end fw-bold">{{ formatPrice(Number(cut.weight_kg) * Number(cut.price_per_kg)) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style="font-size:12px;color:var(--fm-s,#6B6356);">
                <i class="mdi mdi-information-outline me-1"></i>
                {{ cutsTotalKgDetail }} kg total · Grade key: A Prime · B Choice · C Standard · D Utility · Prices include the aggregator's commission. Inspect before you pay — meat sales release to the seller immediately on payment.
              </div>
            </div>

            <!-- Description -->
            <div class="ld-section" v-if="listing.description">
              <h5 class="ld-section-title"><i class="mdi mdi-text me-2"></i>Description</h5>
              <p class="ld-description">{{ listing.description }}</p>
            </div>

            <!-- Dev-Requests #6 — farm / listing location card. Clear where the animal
                 is coming from (county + free-text description + embedded map when
                 GPS is set). Falls back to county only when no GPS is set. -->
            <div class="ld-section" v-if="listing.county_name || listing.location_description || (listing.latitude && listing.longitude)">
              <h5 class="ld-section-title"><i class="mdi mdi-map-marker me-2"></i>Farm Location</h5>
              <div class="ld-location-card">
                <div v-if="listing.farm_name" class="ld-location-farm">
                  <i class="mdi mdi-barn me-1"></i>{{ listing.farm_name }}
                </div>
                <div v-if="listing.county_name" class="ld-location-county">
                  <i class="mdi mdi-map-marker me-1"></i>{{ listing.county_name }} County
                </div>
                <div v-if="listing.location_description" class="ld-location-desc">
                  {{ listing.location_description }}
                </div>
                <!-- Distance from viewer to the farm — asks geolocation on first click.
                     Falls back to a "Show distance" button when we don't have it yet. -->
                <div v-if="distanceKm !== null" class="ld-distance-chip">
                  <i class="mdi mdi-navigation-outline me-1"></i>
                  <strong>{{ distanceKm.toFixed(0) }} km</strong> from your location
                </div>
                <button v-else-if="listing.latitude && listing.longitude" class="ld-distance-btn" @click="computeDistance" :disabled="distanceBusy">
                  <span v-if="distanceBusy"><span class="spinner-border spinner-border-sm me-1"></span>Getting your location…</span>
                  <span v-else><i class="mdi mdi-navigation-outline me-1"></i>Show distance from me</span>
                </button>
                <div v-if="distanceError" style="font-size:11px;color:#B78500;background:rgba(255,171,0,.08);border:1px solid rgba(255,171,0,.28);border-radius:8px;padding:6px 10px;align-self:flex-start;">
                  <i class="mdi mdi-map-marker-alert me-1"></i>{{ distanceError }}
                </div>
                <div v-if="listing.latitude && listing.longitude" class="ld-location-map">
                  <iframe
                    :src="'https://www.openstreetmap.org/export/embed.html?bbox=' + (Number(listing.longitude) - 0.1) + ',' + (Number(listing.latitude) - 0.1) + ',' + (Number(listing.longitude) + 0.1) + ',' + (Number(listing.latitude) + 0.1) + '&layer=mapnik&marker=' + listing.latitude + ',' + listing.longitude"
                    style="border:0;width:100%;height:220px;border-radius:8px;"
                    loading="lazy"
                    referrerpolicy="no-referrer-when-downgrade"></iframe>
                  <a :href="'https://www.google.com/maps/dir/?api=1&destination=' + listing.latitude + ',' + listing.longitude" target="_blank" rel="noopener" class="ld-directions-link">
                    <i class="mdi mdi-directions me-1"></i>Get directions
                  </a>
                </div>
              </div>
            </div>

            <!-- Detailed records gate (shown to non-owners only) -->
            <div v-if="!viewerIsSeller" class="ld-section ld-private-section">
              <h5 class="ld-section-title"><i class="mdi mdi-lock-outline me-2"></i>Detailed records available on request</h5>
              <p class="ld-private-body">
                The seller keeps growth performance, feedlot economics and vaccination history private.
                Interested buyers can ask the seller to share these directly.
              </p>
              <button v-if="!infoRequested" type="button" class="btn btn-outline-warning btn-sm ld-private-btn" :disabled="requestingInfo" @click="requestDetailedInfo">
                <i class="mdi mdi-message-text-outline me-1"></i>
                <span v-if="!requestingInfo">Request detailed records from seller</span>
                <span v-else>Sending request&hellip;</span>
              </button>
              <div v-else class="ld-private-sent">
                <i class="mdi mdi-check-circle me-1"></i>Request sent — the seller will reply in your inbox.
              </div>
            </div>

            <!-- Performance Insight (seller's track record on this animal) — owner only -->
            <div v-if="viewerIsSeller && performance && performance.badge !== 'no_data'" class="ld-section ld-perf-section" :class="'perf-'+performance.badge">
              <h5 class="ld-section-title"><i class="mdi mdi-chart-line me-2"></i>Performance Track Record</h5>
              <div class="ld-perf-head">
                <span class="ld-perf-badge">{{ perfBadgeLabel }}</span>
                <span class="ld-perf-cohort">{{ perfCohortLabel }}</span>
              </div>
              <div v-if="performance.growth_kg_per_day" class="ld-perf-metric-row">
                <div class="ld-perf-bar"><div class="ld-perf-bar-fill" :style="{width: perfBarWidth+'%'}"></div></div>
                <span class="ld-perf-score">{{ Math.round(performance.composite_score) }}/100</span>
              </div>
              <div class="ld-perf-reason">{{ performance.reason }}</div>
              <div class="ld-perf-footnote">
                <i class="mdi mdi-information-outline me-1"></i>Based on the seller's last 90 days of weight readings, ranked against similar animals on their farm.
              </div>
            </div>

            <!-- Feedlot Buy / Don't-Buy verdict — owner only -->
            <div v-if="viewerIsSeller && eligibility" class="ld-section ld-elig-section" :class="'elig-'+eligibility.verdict">
              <h5 class="ld-section-title"><i class="mdi mdi-scale-balance me-2"></i>Feedlot Buy Decision</h5>
              <div class="ld-elig-verdict">
                <i :class="eligVerdictIcon" class="me-2"></i>
                <strong>{{ eligVerdictLabel }}</strong>
                <span v-if="eligibility.formulation" class="ld-elig-ref">vs {{ eligibility.formulation.name }}</span>
              </div>
              <div v-if="eligibility.using_template" class="ld-elig-template-note">
                <i class="mdi mdi-information-outline me-1"></i>Based on Ranch Experts / Patolive Services published feedlot standards. Set up your own formulation in settings to use your numbers.
              </div>
              <ul class="ld-elig-checks">
                <li v-for="(c, ci) in eligibility.checks" :key="'eligck-'+ci" :class="c.pass ? 'pass' : 'fail'">
                  <i :class="c.pass ? 'mdi mdi-check-circle' : 'mdi mdi-alert-circle'"></i>
                  <div>
                    <strong>{{ c.name }}</strong>
                    <span>{{ c.detail }}</span>
                  </div>
                </li>
              </ul>
              <div v-if="eligibility.projection" class="ld-elig-proj">
                <div class="ld-elig-proj-title">Projected feedlot economics</div>
                <div class="ld-elig-proj-grid">
                  <div class="ld-elig-proj-cell"><span>Target weight</span><strong>{{ eligibility.projection.target_weight_kg }} kg</strong></div>
                  <div class="ld-elig-proj-cell"><span>Gain needed</span><strong>{{ eligibility.projection.kg_to_gain }} kg</strong></div>
                  <div class="ld-elig-proj-cell"><span>Projected daily gain</span><strong>{{ eligibility.projection.projected_kg_per_day }} kg/day</strong></div>
                  <div class="ld-elig-proj-cell"><span>Days to target</span><strong>~{{ eligibility.projection.days_to_target }}</strong></div>
                  <div class="ld-elig-proj-cell"><span>Purchase price</span><strong>Ksh {{ Number(eligibility.projection.purchase_price_kshs).toLocaleString() }}</strong></div>
                  <div class="ld-elig-proj-cell"><span>Feed cost</span><strong>Ksh {{ Number(eligibility.projection.feed_cost_kshs).toLocaleString() }}</strong></div>
                  <div class="ld-elig-proj-cell"><span>Projected sale value</span><strong>Ksh {{ Number(eligibility.projection.projected_sale_value_kshs).toLocaleString() }}</strong></div>
                  <div class="ld-elig-proj-cell ld-elig-margin" :class="{positive: eligibility.projection.projected_margin_kshs > 0, negative: eligibility.projection.projected_margin_kshs < 0}">
                    <span>Projected margin</span>
                    <strong>{{ eligibility.projection.projected_margin_kshs >= 0 ? '+' : '' }}Ksh {{ Number(eligibility.projection.projected_margin_kshs).toLocaleString() }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <!-- Health Records — owner only -->
            <div class="ld-section" v-if="viewerIsSeller && healthRecords.length > 0">
              <h5 class="ld-section-title"><i class="mdi mdi-heart-pulse me-2"></i>Health Records</h5>
              <div class="ld-health-list">
                <div class="ld-health-item" v-for="(hr, hi) in healthRecords" :key="'hr-'+hi">
                  <div class="ld-health-icon">
                    <i class="mdi mdi-needle" style="color:#BFA164;"></i>
                  </div>
                  <div>
                    <div class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ hr.vaccine_name }} <span v-if="hr.disease_target" style="color:var(--fm-m,#A09888);font-size:11px;">— {{ hr.disease_target }}</span></div>
                    <div style="color:var(--fm-m,#A09888);font-size:12px;">{{ formatDate(hr.administered_date) }} <span v-if="hr.administered_by">• {{ hr.administered_by }}</span></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tags / Keywords -->
            <div class="ld-section" v-if="listing.tags && listing.tags.length > 0">
              <h5 class="ld-section-title"><i class="mdi mdi-tag-multiple me-2"></i>Tags</h5>
              <div class="d-flex flex-wrap" style="gap:6px;">
                <span class="ld-tag" v-for="(tag, ti) in listing.tags" :key="'tag-'+ti">{{ tag }}</span>
              </div>
            </div>

          </div>

          <!-- ===== RIGHT: PRICE, SELLER, ACTIONS ===== -->
          <div class="col-lg-4">
            <!-- Client issue 17: sticky lives on this wrapper so the whole column
                 (price + seller card) stays visible instead of the action card
                 sliding down and covering the seller profile. -->
            <div class="ld-sidebar-sticky">

            <!-- Price & Action Card -->
            <div class="ld-action-card">
              <!-- Title (desktop only) -->
              <div class="d-none d-lg-block">
                <h4 class="fw-bold mb-2" style="color:var(--fm-h,#F5F0E8);">{{ listing.title }}</h4>
                <div class="d-flex align-items-center flex-wrap mb-3" style="gap:6px;">
                  <span v-if="listing.seller_verified" class="badge bg-success bg-opacity-25 text-success" title="Seller has completed KYC identity verification"><i class="mdi mdi-check-decagram"></i> Seller Verified</span>
                  <span v-if="Number(listing.vet_verified) === 1" class="badge bg-info bg-opacity-25 text-info" title="Animal inspected and verified by a vet on Flockr"><i class="mdi mdi-stethoscope"></i> Vet Verified</span>
                  <span v-if="listing.is_featured" class="badge bg-warning bg-opacity-25 text-warning"><i class="mdi mdi-star"></i> Featured</span>
                  <span class="badge bg-dark" style="border:1px solid var(--fm-br,#2A2520);" v-if="isProduct && listing.product_category_name">{{ listing.product_category_name }}</span>
                  <span class="badge bg-dark" style="border:1px solid var(--fm-br,#2A2520);" v-else-if="listing.listing_type">{{ listing.listing_type === 'batch' ? 'Batch' : 'Individual' }}</span>
                  <span v-if="isMeatListing" class="badge" style="background:#7A2E2E;color:#FFD9D9;border:1px solid #A54848;">
                    <i class="mdi mdi-food-steak me-1"></i>Freshly Slaughtered
                  </span>
                </div>
              </div>

              <!-- Price -->
              <div class="ld-price-section">
                <!-- Track A: pricing modes — Buyer can pick Live or Carcass when both available -->
                <div v-if="hasMultipleBuyModes" class="ld-buy-mode-tabs mb-2">
                  <button v-if="listing.allow_buy_live == 1" class="ld-buy-mode-tab" :class="{active: buyMode==='live'}" @click="buyMode='live'">
                    <i class="mdi mdi-cow me-1"></i>By Live Weight
                  </button>
                  <button v-if="listing.allow_buy_carcass == 1" class="ld-buy-mode-tab" :class="{active: buyMode==='carcass'}" @click="buyMode='carcass'">
                    <i class="mdi mdi-scale-balance me-1"></i>By Carcass Weight
                  </button>
                </div>
                <div class="ld-price-big">{{ listing.currency || 'KES' }} {{ formatPrice(displayTotalPrice) }}
                  <span class="ld-price-unit" v-if="!displayPricePerKg && !listing.price_per_unit">{{ priceUnitLabel(listing.price_unit) }}</span>
                </div>
                <div v-if="displayPricePerKg" class="text-muted" style="font-size:13px;">
                  {{ listing.currency || 'KES' }} {{ formatPrice(displayPricePerKg) }} per kg {{ buyMode === 'carcass' ? 'carcass' : (displayWeight ? 'live (×' + displayWeight + 'kg)' : 'live') }}
                </div>
                <div v-else-if="listing.price_per_unit" class="text-muted" style="font-size:13px;">
                  {{ listing.currency || 'KES' }} {{ formatPrice(listing.price_per_unit) }} per {{ listing.unit || 'kg' }}
                </div>
                <div class="d-flex flex-wrap mt-1" style="gap:6px;">
                  <span v-if="listing.is_negotiable" class="badge bg-warning bg-opacity-10 text-warning" style="font-size:11px;"><i class="mdi mdi-handshake me-1"></i>Negotiable</span>
                  <span v-if="listing.body_condition_score" class="badge ld-bcs-pill" :class="bodyScoreBadge(listing.body_condition_score)" :title="bodyScoreInterpretation(listing.body_condition_score)">
                    <i class="mdi mdi-heart-pulse me-1"></i>BCS {{ Number(listing.body_condition_score) }}/5 · {{ bodyScoreLabel(listing.body_condition_score) }}
                  </span>
                  <span v-if="buyMode === 'carcass' && listing.advertised_dress_pct" class="badge bg-secondary bg-opacity-25 text-secondary" style="font-size:11px;" :title="'Seller-advertised dressing percentage. Final carcass weight confirmed at slaughterhouse.'">
                    <i class="mdi mdi-information-outline me-1"></i>~{{ listing.advertised_dress_pct }}% dress
                  </span>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex flex-column" style="gap:10px;">
                <!-- Sold state — replace Add to Cart with disabled "Sold" pill so buyers
                     who arrive via direct URL / stale tab don't try to checkout an animal
                     another buyer already paid for. Marketplace search already hides sold
                     listings, but listing detail is still reachable. -->
                <button v-if="isSold" class="btn btn-secondary fw-bold btn-lg w-100" disabled>
                  <i class="mdi mdi-cart-off me-1"></i> Sold — no longer available
                </button>
                <button v-else class="btn btn-warning text-dark fw-bold btn-lg w-100" @click="addToCart" :disabled="addingToCart">
                  <span v-if="addingToCart" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else :class="addedToCart ? 'mdi mdi-cart-check' : 'mdi mdi-cart-plus'" class="me-1"></i>
                  {{ addedToCart ? 'Added — view cart' : 'Add to Cart — KES ' + formatPrice(displayTotalPrice) }}
                </button>
                <button class="btn btn-outline-warning w-100" @click="makeOffer">
                  <i class="mdi mdi-handshake me-1"></i> Make an Offer
                </button>
                <button class="btn btn-outline-warning w-100" @click="messageSeller">
                  <i class="mdi mdi-message-text me-1"></i> Message Seller
                </button>
                <button v-if="seller.phone" class="btn btn-outline-secondary w-100 mt-2" @click="callSeller">
                  <i class="mdi mdi-phone me-1"></i> Call Seller
                </button>
                <div class="d-flex" style="gap:8px;">
                  <button class="btn btn-sm flex-fill" :class="listing.is_saved ? 'btn-danger' : 'btn-outline-secondary'" @click="toggleSave">
                    <i :class="listing.is_saved ? 'mdi mdi-heart' : 'mdi mdi-heart-outline'" class="me-1"></i>
                    {{ listing.is_saved ? 'Saved' : 'Save' }}
                  </button>
                  <button class="btn btn-sm btn-outline-secondary flex-fill" @click="shareListing">
                    <i class="mdi mdi-share-variant me-1"></i> Share
                  </button>
                  <button class="btn btn-sm btn-outline-secondary" @click="reportListing">
                    <i class="mdi mdi-flag"></i>
                  </button>
                </div>
              </div>

              <!-- Escrow Trust Badge -->
              <div class="ld-escrow-badge">
                <i class="mdi mdi-shield-lock" style="font-size:20px;color:#34c38f;"></i>
                <div>
                  <div class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:13px;">Escrow Protected</div>
                  <div style="color:var(--fm-m,#A09888);font-size:11px;">Pay safely. Funds held until you confirm delivery.</div>
                </div>
              </div>
            </div>

            <!-- Seller Card -->
            <div class="ld-seller-card">
              <div class="d-flex align-items-center mb-3">
                <div class="ld-seller-avatar-lg">
                  <img v-if="seller.photo_url" :src="photoUrl(seller.photo_url)" :alt="seller.name" class="ld-seller-photo" />
                  <span v-else>{{ getInitials(seller.name || seller.farm_name) }}</span>
                </div>
                <div class="ms-3">
                  <h6 class="fw-bold mb-0" style="color:var(--fm-h,#F5F0E8);">{{ seller.name || seller.farm_name || 'Seller' }}</h6>
                  <div v-if="seller.farm_name && seller.name" style="color:var(--fm-m,#A09888);font-size:12px;">{{ seller.farm_name }}</div>
                  <div class="d-flex align-items-center mt-1" style="gap:8px;">
                  <span v-if="seller.rating" style="color:#f1b44c;font-size:13px;">
                    <i class="mdi mdi-star"></i> {{ seller.rating }}
                  </span>
                    <span v-if="seller.total_reviews" style="color:var(--fm-m,#A09888);font-size:12px;">
                    ({{ seller.total_reviews }} reviews)
                  </span>
                    <span v-if="seller.is_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;" title="Seller has completed KYC identity verification"><i class="mdi mdi-check-decagram"></i> Seller Verified</span>
                  </div>
                </div>
              </div>

              <!-- Seller Stats -->
              <div class="ld-seller-stats">
                <div class="ld-seller-stat" v-if="seller.total_listings">
                  <div class="ld-stat-value">{{ seller.total_listings }}</div>
                  <div class="ld-stat-label">Listings</div>
                </div>
                <div class="ld-seller-stat" v-if="seller.total_sold">
                  <div class="ld-stat-value">{{ seller.total_sold }}</div>
                  <div class="ld-stat-label">Sold</div>
                </div>
                <div class="ld-seller-stat" v-if="seller.member_since">
                  <div class="ld-stat-value">{{ seller.member_since }}</div>
                  <div class="ld-stat-label">Member Since</div>
                </div>
                <div class="ld-seller-stat" v-if="seller.response_time">
                  <div class="ld-stat-value">{{ seller.response_time }}</div>
                  <div class="ld-stat-label">Avg Response</div>
                </div>
              </div>

              <div v-if="seller.county_name" class="mb-3" style="color:var(--fm-m,#A09888);font-size:13px;">
                <i class="mdi mdi-map-marker me-1"></i> {{ seller.county_name }}
              </div>

              <a href="javascript:void(0)" @click="$router.push('/marketplace/seller/' + (seller.id || listing.seller_id))" class="btn btn-warning w-100 btn-sm fw-bold">
                <i class="mdi mdi-storefront me-1"></i> Visit {{ sellerFirstName }}'s Store
              </a>
              <div class="text-center mt-1" style="font-size:11px;color:var(--fm-m,#A09888);">See everything {{ sellerFirstName }} is selling</div>
            </div>

            <!-- Delivery Info -->
            <div class="ld-info-card" v-if="listing.delivery_options">
              <h6 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);font-size:14px;"><i class="mdi mdi-truck-delivery me-2"></i>Delivery</h6>
              <div v-if="listing.delivery_options.pickup" class="ld-delivery-option">
                <i class="mdi mdi-map-marker-radius text-success"></i>
                <div>
                  <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">Pickup Available</div>
                  <div style="color:var(--fm-m,#A09888);font-size:12px;">{{ listing.delivery_options.pickup_location || listing.county_name }}</div>
                </div>
              </div>
              <div v-if="listing.delivery_options.delivery" class="ld-delivery-option">
                <i class="mdi mdi-truck text-warning"></i>
                <div>
                  <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">Delivery Available</div>
                  <div style="color:var(--fm-m,#A09888);font-size:12px;">{{ listing.delivery_options.delivery_info || 'Fees may apply' }}</div>
                </div>
              </div>
              <div v-if="listing.delivery_options.flockr_transport" class="ld-delivery-option">
                <i class="mdi mdi-truck-fast" style="color:#BFA164;"></i>
                <div>
                  <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">Flockr Transport</div>
                  <div style="color:var(--fm-m,#A09888);font-size:12px;">GPS-tracked, welfare-certified delivery</div>
                </div>
              </div>
            </div>

            <!-- Safety Tips -->
            <div class="ld-info-card">
              <h6 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);font-size:14px;"><i class="mdi mdi-shield-alert me-2 text-warning"></i>Safety Tips</h6>
              <ul class="ld-tips-list">
                <li>Always use Flockr escrow for payment protection</li>
                <li>Inspect the animal before confirming delivery</li>
                <li>Check seller ratings and reviews</li>
                <li>Request health certificates and records</li>
              </ul>
            </div>

            <!-- Payment Disclaimer -->
            <div style="background:rgba(255,107,107,0.06);border:1px solid rgba(255,107,107,0.2);border-radius:10px;padding:14px;margin-top:12px;">
              <div style="display:flex;align-items:flex-start;gap:10px;">
                <i class="mdi mdi-alert-circle" style="color:#FF6B6B;font-size:20px;flex-shrink:0;margin-top:1px;"></i>
                <div>
                  <div style="color:#FF6B6B;font-size:13px;font-weight:700;margin-bottom:4px;">Do NOT pay the seller directly</div>
                  <div style="color:var(--fm-m,#A09888);font-size:12px;line-height:1.5;">All payments must be made through the Flockr platform. Direct payments to sellers are not protected and Flockr cannot assist with disputes for transactions made outside the system.</div>
                </div>
              </div>
            </div>

            <!-- Terms -->
            <div style="text-align:center;padding:12px 0 0;margin-top:12px;">
              <p style="color:#4A4540;font-size:11px;line-height:1.5;margin:0;">By using Flockr, you agree to our <a href="javascript:void(0)" style="color:#BFA164;text-decoration:underline;">Terms &amp; Conditions</a> and <a href="javascript:void(0)" style="color:#BFA164;text-decoration:underline;">Privacy Policy</a>.</p>
            </div>

            </div>
          </div>
        </div>

        <!-- ========== RELATED LISTINGS ========== -->
        <section class="mt-5 pt-4" style="border-top:1px solid var(--fm-br);" v-if="relatedListings.length > 0">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5 class="fw-bold mb-0" style="color:var(--fm-h,#F5F0E8);">Similar Listings</h5>
            <a href="javascript:void(0)" @click="$router.push('/marketplace/search?species=' + (listing.species_id || ''))" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">See More <i class="mdi mdi-arrow-right"></i></a>
          </div>
          <div class="row g-3">
            <div class="col-lg-3 col-md-4 col-6" v-for="(rl, rli) in relatedListings" :key="'rl-'+rli">
              <div class="ld-related-card" @click="goToListing(rl.id)">
                <div class="ld-related-img">
                  <img v-if="rl.primary_photo" :src="photoUrl(rl.primary_photo)" :alt="rl.title" style="width:100%;height:100%;object-fit:cover;" />
                  <i v-else :class="getSpeciesIcon(rl.species_name)" style="font-size:40px;color:rgba(191,161,100,0.3);"></i>
                </div>
                <div class="p-3">
                  <h6 class="fw-bold mb-1" style="color:var(--fm-h,#F5F0E8);font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ rl.title }}</h6>
                  <div style="color:var(--fm-m,#A09888);font-size:11px;" class="mb-1"><i class="mdi mdi-map-marker"></i> {{ rl.county_name || rl.location }}</div>
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="fw-bold" style="color:#BFA164;font-size:15px;">{{ rl.currency || 'KES' }} {{ formatPrice(rl.price) }}</div>
                    <div class="d-flex align-items-center" style="gap:3px;font-size:11px;color:var(--fm-m,#A09888);">
                      <div class="ld-mini-avatar">{{ getInitials(rl.seller_name) }}</div>
                      <span>{{ rl.seller_name }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>

      <!-- ========== MOBILE BOTTOM BAR ========== -->
      <div class="ld-mobile-bar d-lg-none" v-if="listing.id && !loading">
        <div class="ld-mobile-price">
          <div class="fw-bold" style="color:#BFA164;font-size:18px;">{{ listing.currency || 'KES' }} {{ formatPrice(listing.price) }}</div>
          <div style="color:var(--fm-m,#A09888);font-size:11px;" v-if="listing.is_negotiable">Negotiable</div>
        </div>
        <div class="d-flex" style="gap:8px;">
          <button class="btn btn-outline-warning btn-sm" @click="messageSeller"><i class="mdi mdi-message-text"></i></button>
          <!-- Sticky-bar Add to Cart — flips to "Sold" when listing is no longer available
               (same gating as the inline pill above; see isSold computed). -->
          <button v-if="isSold" class="btn btn-secondary fw-bold btn-sm px-3" disabled>
            <i class="mdi mdi-cart-off me-1"></i> Sold
          </button>
          <button v-else class="btn btn-warning text-dark fw-bold btn-sm px-3" @click="addToCart" :disabled="addingToCart">
            <i :class="addedToCart ? 'mdi mdi-cart-check' : 'mdi mdi-cart-plus'" class="me-1"></i>
            {{ addedToCart ? 'In cart' : 'Add to Cart' }}
          </button>
          <button v-if="!isSold" class="btn btn-outline-warning btn-sm px-3" @click="makeOffer">
            <i class="mdi mdi-handshake me-1"></i> Offer
          </button>
        </div>
      </div>

      <div class="rightbar-overlay"></div>
    </div>
  </div>
</template>

<script>
import { execute, executeJson, flocker, uploads } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

export default {
  name: "ListingDetail",
  components: { UserAvatar },

  data() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
      themeObserver: null,
      myAvatar: sessionStorage.getItem('userAvatar') || '',
      myName: sessionStorage.getItem('userName') || '',

      listing: {},
      seller: {},
      photos: [],
      // Farm-to-viewer distance (Dev-Requests #6 extension)
      distanceKm: null,
      distanceBusy: false,
      distanceError: '',
      healthRecords: [],
      relatedListings: [],
      activePhotoIndex: 0,
      loading: false,
      addingToCart: false,
      addedToCart: false,
      performance: null,
      eligibility: null,
      requestingInfo: false,
      infoRequested: false,
      buyMode: 'live'
    };
  },

  computed: {
    isProduct() {
      return !!this.listing && this.listing.listing_kind === 'livestock_product';
    },
    // "200 pieces", "50 kg" — quantity noun derived from the price unit.
    productUnitNoun() {
      var u = String((this.listing && this.listing.price_unit) || '');
      switch (u) {
        case 'per_kg': case 'per_kg_live': case 'per_kg_dressed': return 'kg';
        case 'per_liter': case 'per_litre': return 'litres';
        case 'per_piece': case 'per_head': return 'pieces';
        case 'per_dozen': return 'dozen';
        case 'per_batch': return 'lots';
        default: {
          var n = u.replace(/^per_/, '').replace(/_/g, ' ');
          return n ? n + 's' : 'units';
        }
      }
    },
    viewerIsSeller() {
      var uid = sessionStorage.getItem('userId') || '';
      return !!this.listing && !!this.listing.seller_id && String(this.listing.seller_id) === String(uid);
    },
    // Butchery workflow — category 105 = Meat (Carcass), the aggregator flow.
    isMeatListing() {
      return !!this.listing && Number(this.listing.product_category_id) === 105;
    },
    cutsTotalKgDetail() {
      var kg = (this.listing && this.listing.cuts || []).reduce(function(s, c) { return s + (Number(c.weight_kg) || 0); }, 0);
      return Math.round(kg * 10) / 10;
    },
    // First word of the seller's display name — drives the "Visit X's Store" CTA so it
    // reads personally (Amazon "Visit the Store" pattern). Falls back to "the seller".
    sellerFirstName() {
      var n = (this.seller && (this.seller.name || this.seller.farm_name)) || '';
      if (!n) return 'the seller';
      return String(n).trim().split(/\s+/)[0] || 'the seller';
    },
    // True once the listing has been sold (or is fully out of stock). Drives the
    // "Sold — no longer available" pill on the action card.
    // NULL available_quantity means quantity is untracked (single-animal listing) — treat as in stock.
    // Mirror of backend addToCart fix from 2026-06-12: NULL must not coerce to 0.
    isSold() {
      if (!this.listing) return false;
      if (this.listing.status === 'sold') return true;
      if (this.listing.status !== 'active') return false;
      var q = this.listing.available_quantity;
      if (q === null || q === undefined || q === '') return false;
      return Number(q) <= 0;
    },
    // Track A — pricing display + buy-mode helpers
    hasMultipleBuyModes() {
      return (this.listing.allow_buy_live == 1 || this.listing.allow_buy_live === true)
          && (this.listing.allow_buy_carcass == 1 || this.listing.allow_buy_carcass === true);
    },
    displayWeight() {
      if (this.buyMode === 'carcass') {
        var lw = Number(this.listing.weight_kg) || 0;
        var dp = Number(this.listing.advertised_dress_pct) || 0;
        if (lw > 0 && dp > 0) return Math.round(lw * dp / 100 * 10) / 10;
        return null;
      }
      return Number(this.listing.weight_kg) || null;
    },
    displayPricePerKg() {
      if (this.buyMode === 'carcass') return Number(this.listing.price_per_kg_carcass_kes) || null;
      return Number(this.listing.price_per_kg_live_kes) || null;
    },
    displayTotalPrice() {
      if (this.displayPricePerKg && this.displayWeight) {
        return Math.round(this.displayPricePerKg * this.displayWeight);
      }
      return Number(this.listing.price) || 0;
    },
    perfBadgeLabel(){if(!this.performance)return '';var m={star:'Star Performer',on_track:'On Track',watch:'Watch',at_risk:'Falling behind',establishing:'Establishing baseline',no_data:'No data yet'};return m[this.performance.badge]||this.performance.badge},
    perfCohortLabel(){if(!this.performance||!this.performance.cohort_key)return '';var parts=String(this.performance.cohort_key).split(':');var spMap={'1':'Cattle','2':'Goats','3':'Sheep','4':'Pigs','5':'Camels','6':'Donkeys','7':'Chickens'};var sp=spMap[parts[0]]||'Animals';var band=parts[1]==='unknown'?'age unknown':parts[1];var sex=parts[2]&&parts[2]!=='unknown'?parts[2]:'';return 'vs '+sp+(band?' • '+band:'')+(sex?' • '+sex:'')+' on seller\'s farm'},
    perfBarWidth(){if(!this.performance||this.performance.composite_score===null||this.performance.composite_score===undefined)return 0;return Math.max(2,Math.min(100,Number(this.performance.composite_score)))},
    eligVerdictLabel(){if(!this.eligibility)return '';var m={buy:'Strong buy',review:'Negotiate or review carefully',pass:'Pass — does not meet your criteria',unknown:'Insufficient data'};return m[this.eligibility.verdict]||this.eligibility.verdict},
    eligVerdictIcon(){if(!this.eligibility)return '';var m={buy:'mdi mdi-check-decagram',review:'mdi mdi-alert-outline',pass:'mdi mdi-close-octagon',unknown:'mdi mdi-help-circle-outline'};return m[this.eligibility.verdict]||'mdi mdi-information'},
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    activePhoto() {
      if (this.photos.length === 0) return null;
      var photo = this.photos[this.activePhotoIndex];
      return photo ? this.photoUrl(photo.photo_url || photo) : null;
    },
    activePhotoMeta() {
      if (this.photos.length === 0) return null;
      return this.photos[this.activePhotoIndex] || null;
    },
    activePhotoTimestamp() {
      var p = this.activePhotoMeta; if (!p) return '';
      var ts = p.taken_at || p.created_at; if (!ts) return '';
      var d = new Date(String(ts).replace(' ', 'T'));
      if (isNaN(d)) return '';
      var label = p.taken_at ? 'Taken' : 'Uploaded';
      return label + ' ' + d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    },
    activePhotoTimestampTitle() {
      var p = this.activePhotoMeta; if (!p) return '';
      if (p.taken_at) return 'Photo taken on ' + p.taken_at + ' (from EXIF / file metadata)';
      if (p.created_at) return 'Uploaded ' + p.created_at + ' (camera-taken time not available)';
      return '';
    }
  },

  watch: {
    '$route.params.id': function(newId) {
      if (newId) {
        this.loadListing(newId);
      }
    }
  },

  mounted() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    var listingId = this.$route.params.id;
    if (listingId) {
      this.loadListing(listingId);
    }
  },

  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },

  methods: {
    // Butchery workflow — Flockr A-D quality scale (same key as slaughter sheets).
    gradeLabel(g) {
      return { A: 'Prime', B: 'Choice', C: 'Standard', D: 'Utility' }[String(g || '').toUpperCase()] || '';
    },
    gradeBadgeStyle(g) {
      var styles = {
        A: 'background:#1E4620;color:#7BE38B;border:1px solid #2F6B33;',
        B: 'background:#3D3A1E;color:#E3D97B;border:1px solid #6B662F;',
        C: 'background:#3A2E1E;color:#E3B87B;border:1px solid #6B542F;',
        D: 'background:#3A1E1E;color:#E38B7B;border:1px solid #6B2F2F;'
      };
      return styles[String(g || '').toUpperCase()] || 'background:#2A2520;color:#A09888;border:1px solid #3A352F;';
    },
    // Dev-Requests #6 extension — compute great-circle distance between the buyer's
    // browser location and the farm's GPS pin. Falls back gracefully when
    // getCurrentPosition is blocked (insecure origin, denied permission, etc.).
    computeDistance() {
      if (!this.listing.latitude || !this.listing.longitude) return;
      this.distanceError = '';
      if (!navigator.geolocation) { this.distanceError = 'Location not supported by this browser.'; return; }
      const isSecure = window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
      if (!isSecure) { this.distanceError = 'Distance requires an HTTPS site.'; return; }
      this.distanceBusy = true;
      const self = this;
      navigator.geolocation.getCurrentPosition(function(pos){
        const bLat = Number(self.listing.latitude), bLng = Number(self.listing.longitude);
        const uLat = pos.coords.latitude, uLng = pos.coords.longitude;
        const toRad = d => d * Math.PI / 180;
        const R = 6371;
        const dLat = toRad(bLat - uLat), dLng = toRad(bLng - uLng);
        const a = Math.sin(dLat/2)**2 + Math.cos(toRad(uLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng/2)**2;
        self.distanceKm = 2 * R * Math.asin(Math.sqrt(a));
        self.distanceBusy = false;
      }, function(err){
        self.distanceBusy = false;
        const reason = err && err.code === 1 ? 'you blocked location access' :
                       err && err.code === 2 ? 'position unavailable' :
                       err && err.code === 3 ? 'took too long' :
                       (err && err.message) || 'unknown error';
        self.distanceError = 'Could not get your location (' + reason + ').';
      }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
    },

    // Body condition score helpers (1–5 livestock scale; <=2 thin, 2.5–3.5 ideal, >=4 over-conditioned)
    bodyScoreLabel(s) {
      var n = Number(s); if (isNaN(n)) return '';
      if (n <= 2) return 'Thin';
      if (n <= 3.5) return 'Ideal';
      return 'Over-conditioned';
    },
    bodyScoreBadge(s) {
      var n = Number(s); if (isNaN(n)) return 'bg-secondary';
      if (n <= 2) return 'bg-warning text-dark';
      if (n <= 3.5) return 'bg-success';
      return 'bg-warning text-dark';
    },
    bodyScoreInterpretation(s) {
      var n = Number(s); if (isNaN(n)) return '';
      return 'Body Condition Score ' + n + ' / 5. ' + (n <= 2 ? 'Underweight — feeding needed.' : n <= 3.5 ? 'Ideal condition.' : 'Over-conditioned — at risk of metabolic issues.');
    },
    goBack() {
      if (window.history.length > 1) this.$router.go(-1);
      else this.$router.push('/marketplace');
    },
    syncTheme() {
      var t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark';
      window.__flockrSetTheme(t);
      this.darkMode = t !== 'light';

      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }

      var el = this.$el;
      if (el) {
        el.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        el.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },

    photoUrl(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},

    // SEO — per-listing meta + Product structured data. Only fires after the
    // listing is loaded so we can use its real title/price/photo.
    applySeo(){
      var l = this.listing || {};
      if (!l.id) return;
      var title = (l.title || 'Livestock') + ' · ' + (l.currency || 'KES') + ' ' + this.formatPrice(l.price) + ' | Flockr';
      var primary = (this.photos && this.photos[0] && this.photoUrl(this.photos[0].photo_url || this.photos[0])) || 'https://flockr.africa/assets/images/flockr_logo.png';
      var descBits = [];
      if (l.species_name) descBits.push(l.species_name);
      if (l.breed_name)   descBits.push(l.breed_name);
      if (l.weight_kg)    descBits.push(l.weight_kg + 'kg');
      if (l.age_months)   descBits.push(l.age_months + ' months old');
      if (l.county_name)  descBits.push('in ' + l.county_name);
      var desc = (descBits.join(' · ') || 'Livestock for sale on Flockr — Africa\'s livestock marketplace') + '. ' + (l.description || '').substring(0, 140);
      // Product structured data — makes the listing eligible for Google rich cards
      var jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': l.title,
        'image': primary,
        'description': (l.description || desc).substring(0, 500),
        'sku': l.id,
        'category': l.species_name || 'Livestock',
        'offers': {
          '@type': 'Offer',
          'url': window.location.href,
          'priceCurrency': l.currency || 'KES',
          'price': Number(l.price) || 0,
          'availability': l.status === 'active' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          'seller': { '@type': 'Person', 'name': l.seller_name || 'Flockr seller' }
        }
      };
      if (this.$updateHead) this.$updateHead({ title: title, description: desc, image: primary, jsonLd: jsonLd });
    },


    async loadListing(id) {
      this.loading = true;
      this.listing = {};
      this.seller = {};
      this.photos = [];
      this.healthRecords = [];
      this.relatedListings = [];
      this.activePhotoIndex = 0;
      this.performance = null;
      this.eligibility = null;
      window.scrollTo({ top: 0 });

      try {
        await this.fetchListingDetail(id);
        if (this.listing.id) {
          this.fetchSellerInfo();
          this.fetchRelatedListings();
          if (this.listing.animal_id) this.fetchListingPerformance(this.listing.animal_id);
          this.fetchFeedlotEligibility(this.listing.id);
        }
      } catch (e) {
        console.error('Error loading listing:', e);
      } finally {
        this.loading = false;
      }
    },

    async fetchListingPerformance(animalId) {
      try {
        var r = await executeJson({ function: 'getAnimalPerformanceScore', animalId: animalId }, flocker);
        if (r.data && r.data.success) this.performance = r.data.data && r.data.data.score ? r.data.data.score : null;
      } catch (e) { this.performance = null; }
    },

    async fetchFeedlotEligibility(listingId) {
      try {
        var r = await executeJson({ function: 'getListingFeedlotEligibility', listingId: listingId, viewerId: sessionStorage.getItem('userId') || '' }, flocker);
        if (r.data && r.data.success) this.eligibility = r.data.data;
      } catch (e) { this.eligibility = null; }
    },

    // ============ API CALLS ============

    async fetchListingDetail(id) {
      var viewerId = sessionStorage.getItem('userId') || '';
      try {
        var response = await executeJson({
          function: "getListing",
          listingId: id,
          viewerId: viewerId
        }, flocker);
        if (response.data.success) {
          this.listing = response.data.data.listing || {};
          this.photos = this.listing.photos || [];
          if (this.photos.length === 0 && this.listing.photo_url) {
            this.photos = [{ photo_url: this.listing.photo_url }];
          }
          this.healthRecords = this.listing.vaccinations || [];
          // SEO (Alex 2026-07-22) — set the tab title, meta description,
          // og-tags and Product JSON-LD so Google indexes this listing as a
          // rich card.
          this.applySeo();
          // GA4 view_item event (Alex 2026-07-23) — funnel start
          if (this.$trackEvent) {
            this.$trackEvent('view_item', {
              currency: this.listing.currency || 'KES',
              value: Number(this.listing.price) || 0,
              items: [{
                item_id: this.listing.id,
                item_name: this.listing.title,
                item_category: this.listing.species_name || 'livestock',
                item_brand: this.listing.breed_name || undefined,
                price: Number(this.listing.price) || 0,
                quantity: 1
              }]
            });
          }
          // V2.c — seed margin defaults from this listing so the calculator is useful on first paint
          var p = Number(this.listing.price) || 0;
          if (p > 0) {
            this.margin.buyPrice = p;
            this.margin.sellPrice = Math.round(p * 1.25); // sensible default: aim for a 25% gross before costs
          }
        }
      } catch (e) {
        console.error('Error fetching listing detail:', e);
      }
    },

    async fetchSellerInfo() {
      var sellerId = this.listing.seller_id;
      if (!sellerId) return;
      try {
        var response = await executeJson({
          function: "getUserProfile",
          userId: sellerId,
          viewerId: sessionStorage.getItem('userId') || ''
        }, flocker);
        if (response.data.success) {
          var user = response.data.data.user || {};
          var ratingStats = (user.rating_stats || []).find(function(r) { return r.role_context === 'seller'; }) || {};
          var isVerified = (user.verifications || []).some(function(v) { return v.verification_status === 'verified'; });
          this.seller = {
            id: user.id,
            name: user.display_name || (user.first_name + ' ' + user.last_name),
            farm_name: '',
            phone: user.phone,
            photo_url: user.avatar_url,
            county_name: user.county_name,
            rating: ratingStats.avg_overall || null,
            total_reviews: ratingStats.total_reviews || 0,
            is_verified: isVerified,
            total_listings: 0,
            total_sold: 0,
            member_since: user.created_at ? new Date(user.created_at).getFullYear().toString() : '',
            response_time: ''
          };
        }
      } catch (e) {
        console.error('Error fetching seller info:', e);
      }
    },

    async fetchRelatedListings() {
      try {
        var response = await executeJson({
          function: "searchListings",
          speciesId: this.listing.species_id || '',
          limit: 4,
          page: 1,
          sortBy: "popular"
        }, flocker);
        if (response.data.success) {
          this.relatedListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching related listings:', e);
      }
    },

    async toggleSave() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      try {
        var response = await executeJson({
          function: "saveListing",
          userId: userId,
          listingId: this.listing.id
        }, flocker);
        if (response.data.success) {
          this.listing.is_saved = !this.listing.is_saved;
        }
      } catch (e) {
        console.error('Error toggling save:', e);
      }
    },

    // ============ ACTIONS ============

    async addToCart() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.$router.push('/login'); return; }
      // If already added in this session, second click goes to cart
      if (this.addedToCart) { this.$router.push('/cart'); return; }
      this.addingToCart = true;
      try {
        var r = await executeJson({ function: 'addToCart', userId: userId, listingId: this.listing.id, quantity: 1 }, flocker);
        if (r.data && r.data.success) {
          this.addedToCart = true;
          // Bubble new count to topbar listeners + persist for next page load
          var count = (r.data.data && r.data.data.cartCount) || 0;
          sessionStorage.setItem('flockr_cart_count', count);
          window.dispatchEvent(new CustomEvent('flockr-cart-updated', { detail: { count: count } }));
          // GA4 add_to_cart event (Alex 2026-07-23)
          if (this.$trackEvent) {
            this.$trackEvent('add_to_cart', {
              currency: this.listing.currency || 'KES',
              value: Number(this.listing.price) || 0,
              items: [{
                item_id: this.listing.id,
                item_name: this.listing.title,
                item_category: this.listing.species_name || 'livestock',
                price: Number(this.listing.price) || 0,
                quantity: 1
              }]
            });
          }
        } else {
          alert((r.data && r.data.message) || 'Could not add to cart');
        }
      } catch (e) {
        alert('Network error');
      }
      this.addingToCart = false;
    },
    makeOffer() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      this.$router.push('/marketplace/offer/' + this.listing.id);
    },

    async requestDetailedInfo() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.$router.push('/login'); return; }
      var sellerId = this.listing.seller_id;
      if (!sellerId || sellerId === 'undefined') { alert('Seller info not available.'); return; }
      this.requestingInfo = true;
      var msg = 'Hi, I am interested in your listing "' + (this.listing.title || '') + '". Could you please share the growth performance, feedlot economics and vaccination/health records for this animal? Thanks.';
      try {
        var f = new FormData();
        f.append('function', 'sendMessage');
        f.append('senderId', userId);
        f.append('receiverId', sellerId);
        f.append('content', msg);
        f.append('messageType', 'text');
        f.append('contextType', 'listing');
        f.append('contextId', this.listing.id);
        f.append('listingId', this.listing.id);
        await execute(f, flocker);
        this.infoRequested = true;
      } catch (e) {
        alert('Could not send request. Please try again.');
      }
      this.requestingInfo = false;
    },

    async messageSeller() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.$router.push('/login'); return; }
      var sellerId = this.listing.seller_id;
      if (!sellerId || sellerId === 'undefined') { alert('Seller info not available.'); return; }
      var photo = '';
      if (this.photos.length > 0 && this.photos[0].photo_url) photo = this.photos[0].photo_url;
      else if (this.listing.photo_url) photo = this.listing.photo_url;
      var cardData = JSON.stringify({
        _type: 'listing_card',
        listing_id: this.listing.id,
        title: this.listing.title || '',
        price: this.listing.price || 0,
        photo: photo,
        species: this.listing.species_name || '',
        breed: this.listing.breed_name || '',
        text: 'Hi, I am interested in this listing. Is it still available?'
      });
      try {
        var f = new FormData();
        f.append('function', 'sendMessage');
        f.append('senderId', userId);
        f.append('receiverId', sellerId);
        f.append('content', cardData);
        f.append('messageType', 'listing_card');
        f.append('contextType', 'listing');
        f.append('contextId', this.listing.id);
        f.append('listingId', this.listing.id);
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data.conversationId) {
          this.$router.push('/messages/' + r.data.data.conversationId);
        } else { this.$router.push('/messages'); }
      } catch (e) { this.$router.push('/messages'); }
    },
    callSeller() {
      if (this.seller.phone) { window.open('tel:' + this.seller.phone); }
    },

    shareListing() {
      var url = window.location.href;
      if (navigator.share) {
        navigator.share({
          title: this.listing.title,
          text: 'Check out this listing on Flockr: ' + this.listing.title + ' - KES ' + this.formatPrice(this.listing.price),
          url: url
        }).catch(function() {});
      } else {
        navigator.clipboard.writeText(url).then(function() {
          alert('Link copied to clipboard!');
        }).catch(function() {
          prompt('Copy this link:', url);
        });
      }
    },

    reportListing() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      var reason = prompt('Why are you reporting this listing?');
      if (reason) {
        executeJson({
          function: "submitReport",
          reporterId: userId,
          reportedType: "listing",
          reportedId: this.listing.id,
          reason: reason
        }, flocker).then(function() {
          alert('Report submitted. Thank you.');
        }).catch(function() {
          alert('Failed to submit report. Please try again.');
        });
      }
    },

    goToListing(id) {
      this.$router.push('/marketplace/listing/' + id);
    },

    // ============ PHOTO NAVIGATION ============

    prevPhoto() {
      if (this.activePhotoIndex > 0) {
        this.activePhotoIndex--;
      } else {
        this.activePhotoIndex = this.photos.length - 1;
      }
    },

    nextPhoto() {
      if (this.activePhotoIndex < this.photos.length - 1) {
        this.activePhotoIndex++;
      } else {
        this.activePhotoIndex = 0;
      }
    },

    // ============ HELPERS ============

    formatPrice(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-KE');
    },

    // Pilot-reviewer fix (issue #2): unit label resolver — mirrors the same
    // helper on ListingSearchResults / MarketplaceHome / Android / iOS.
    priceUnitLabel(u) {
      switch (u) {
        case 'per_kg_live':    return '/ kg live';
        case 'per_kg_dressed': return '/ kg dressed';
        case 'per_batch':      return '/ batch';
        case 'per_liter':
        case 'per_litre':      return '/ litre';
        case 'per_head':       return '/ head';
        // product units (per_piece, per_tray, per_kg, per_dozen, ...)
        default:               return u ? '/ ' + String(u).replace(/^per_/, '').replace(/_/g, ' ') : '';
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      var d = new Date(dateStr);
      return d.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    getInitials(name) {
      if (!name) return '?';
      var parts = name.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },

    getSpeciesIcon(speciesName) {
      if (!speciesName) return 'mdi mdi-paw';
      var s = speciesName.toLowerCase();
      if (s.includes('cattle') || s.includes('cow') || s.includes('bull') || s.includes('heifer')) return 'mdi mdi-cow';
      if (s.includes('goat')) return 'mdi mdi-alpha-g-circle';
      if (s.includes('sheep') || s.includes('ram') || s.includes('ewe') || s.includes('dorper')) return 'mdi mdi-sheep';
      if (s.includes('poultry') || s.includes('chicken') || s.includes('kienyeji') || s.includes('bird')) return 'mdi mdi-bird';
      if (s.includes('pig') || s.includes('sow') || s.includes('boar')) return 'mdi mdi-pig';
      if (s.includes('camel')) return 'mdi mdi-alpha-c-circle';
      return 'mdi mdi-paw';
    },

    truncate(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    }
  }
};
</script>

<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style>
html[data-theme="light"] body {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}
html[data-theme="dark"] body {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}
html[data-theme="light"] .listing-detail-root,
html[data-theme="light"] .ld-layout-wrapper,
html[data-theme="light"] #layout-wrapper.ld-layout-wrapper {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}
html[data-theme="dark"] .listing-detail-root,
html[data-theme="dark"] .ld-layout-wrapper,
html[data-theme="dark"] #layout-wrapper.ld-layout-wrapper {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}

html[data-theme="light"] .listing-detail-root nav.mb-3,
html[data-theme="light"] .listing-detail-root nav.mb-3 ol,
html[data-theme="light"] .listing-detail-root .breadcrumb {
  background:#F7F5F2!important;
}

</style>

<style scoped>
.listing-detail-root, .ld-layout-wrapper { background:var(--fm-bg2,#0F0F0F)!important; min-height:100vh; color:var(--fm-h,#F5F0E8)!important; }

/* TOP BAR */
.ld-topbar { background:var(--fm-bg1,#141414)!important; border-bottom:1px solid var(--fm-br,#2A2520); padding:10px 0; position:sticky; top:0; z-index:1040; }
.ld-back{width:36px;height:36px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F)!important;color:var(--fm-h,#F5F0E8);font-size:19px;flex-shrink:0;display:flex;align-items:center;justify-content:center}
.ld-nav-link { color:var(--fm-m,#A09888)!important; text-decoration:none; font-size:14px; font-weight:500; }
.ld-nav-link:hover { color:#BFA164!important; }

/* BREADCRUMB */
.ld-breadcrumb { background:transparent; padding:0; margin:0; font-size:13px; }
.ld-breadcrumb .breadcrumb-item a { color:var(--fm-m,#A09888)!important; text-decoration:none; }
.ld-breadcrumb .breadcrumb-item a:hover { color:#BFA164!important; }
.ld-breadcrumb .breadcrumb-item.active { color:var(--fm-m,#A09888)!important; }
.ld-breadcrumb .breadcrumb-item+.breadcrumb-item::before { color:var(--fm-m,#A09888)!important; }


/* BREADCRUMB */
.ld-breadcrumb,
.ld-breadcrumb.breadcrumb {
  background:transparent!important;
  padding:0!important;
  margin:0!important;
  font-size:13px;
  box-shadow:none!important;
  border:none!important;
}
.ld-breadcrumb .breadcrumb-item a {
  color:var(--fm-m,#A09888)!important;
  text-decoration:none;
}
.ld-breadcrumb .breadcrumb-item a:hover {
  color:#BFA164!important;
}
.ld-breadcrumb .breadcrumb-item.active {
  color:var(--fm-m,#A09888)!important;
}
.ld-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
  color:var(--fm-m,#A09888)!important;
}
html[data-theme="light"] .ld-breadcrumb,
html[data-theme="light"] .ld-breadcrumb.breadcrumb {
  background:#F7F5F2!important;
}
html[data-theme="dark"] .ld-breadcrumb,
html[data-theme="dark"] .ld-breadcrumb.breadcrumb {
  background:transparent!important;
}

/* PHOTO GALLERY */
.ld-gallery { border-radius:16px; overflow:hidden; background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); }
.ld-main-photo { position:relative; width:100%; height:400px; background:linear-gradient(135deg,#1A1510,#0F0F0F); display:flex; align-items:center; justify-content:center; overflow:hidden; }
.ld-main-img { width:100%; height:100%; object-fit:cover; }
.ld-main-placeholder { display:flex; flex-direction:column; align-items:center; justify-content:center; }
.ld-photo-nav { position:absolute; top:50%; transform:translateY(-50%); width:40px; height:40px; border-radius:50%; background:rgba(0,0,0,.6)!important; border:none; color:#fff!important; font-size:22px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .2s; }
.ld-photo-nav:hover { background:rgba(0,0,0,.8)!important; }
.ld-photo-prev { left:12px; }
.ld-photo-next { right:12px; }
.ld-photo-counter { position:absolute; bottom:12px; right:12px; background:rgba(0,0,0,.6)!important; color:#fff!important; padding:4px 12px; border-radius:50px; font-size:12px; font-weight:600; }
.ld-photo-timestamp { position:absolute; bottom:12px; left:12px; background:rgba(0,0,0,.6)!important; color:#fff!important; padding:4px 10px; border-radius:50px; font-size:11px; font-weight:500; display:inline-flex; align-items:center; }
.ld-photo-badges { position:absolute; top:12px; left:12px; display:flex; gap:6px; }
.ld-thumbnails { display:flex; gap:8px; padding:12px; overflow-x:auto; }
.ld-thumb { width:72px; height:56px; border-radius:8px; overflow:hidden; border:2px solid transparent; cursor:pointer; flex-shrink:0; opacity:0.6; transition:all .2s; }
.ld-thumb.active { border-color:#BFA164; opacity:1; }
.ld-thumb:hover { opacity:1; }
.ld-thumb img { width:100%; height:100%; object-fit:cover; }

/* SECTIONS */
.ld-section { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:14px; padding:20px; margin-bottom:16px; }
.ld-section-title { color:var(--fm-h,#F5F0E8)!important; font-size:16px; font-weight:700; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid var(--fm-br,#2A2520); }

/* Dev-Requests #6 — farm / listing location card */
.ld-location-card { display:flex; flex-direction:column; gap:8px; }
.ld-distance-chip { display:inline-flex; align-items:center; gap:4px; background:rgba(80,200,120,.14); color:#50C878; font-size:13px; padding:6px 12px; border-radius:20px; align-self:flex-start; }
.ld-distance-btn { background:transparent; border:1px solid #BFA164; color:#BFA164; font-size:12px; padding:6px 12px; border-radius:20px; cursor:pointer; align-self:flex-start; transition:all .15s; }
.ld-distance-btn:hover { background:rgba(191,161,100,.12); }
.ld-distance-btn:disabled { opacity:.6; cursor:wait; }
.ld-location-farm { color:var(--fm-h,#F5F0E8); font-weight:700; font-size:15px; }
.ld-location-county { color:var(--fm-h,#F5F0E8); font-size:13px; font-weight:600; }
.ld-location-desc { color:var(--fm-m,#A09888); font-size:12px; line-height:1.5; }
.ld-location-map { position:relative; }
.ld-directions-link { display:inline-block; margin-top:8px; padding:6px 12px; background:rgba(191,161,100,.15); color:#BFA164; font-weight:600; font-size:12px; border-radius:6px; text-decoration:none; }
.ld-directions-link:hover { background:rgba(191,161,100,.25); text-decoration:none; }

/* PRIVATE RECORDS GATE */
.ld-private-section { border-left:4px solid #BFA164; }
.ld-private-body { color:var(--fm-m,#A09888); font-size:14px; line-height:1.55; margin-bottom:14px; }
.ld-private-btn { border-color:#BFA164; color:#BFA164; }
.ld-private-btn:hover:not(:disabled) { background:#BFA164; color:#0A0A0A; }
.ld-private-sent { color:#50C878; font-size:13px; display:flex; align-items:center; gap:4px; }

/* PERFORMANCE INSIGHT */
.ld-perf-section.perf-star { border-left:4px solid #50C878; }
.ld-perf-section.perf-on_track { border-left:4px solid #4FC3F7; }
.ld-perf-section.perf-watch { border-left:4px solid #FF9800; }
.ld-perf-section.perf-at_risk { border-left:4px solid #FF6B6B; }
.ld-perf-section.perf-establishing { border-left:4px solid #6B6356; }
.ld-perf-head { display:flex; align-items:center; gap:10px; margin-bottom:12px; flex-wrap:wrap; }
.ld-perf-badge { font-size:12px; font-weight:700; padding:5px 12px; border-radius:999px; background:rgba(191,161,100,.15); color:#BFA164; }
.perf-star .ld-perf-badge { background:rgba(80,200,120,.18); color:#50C878; }
.perf-on_track .ld-perf-badge { background:rgba(79,195,247,.18); color:#4FC3F7; }
.perf-watch .ld-perf-badge { background:rgba(255,152,0,.18); color:#FF9800; }
.perf-at_risk .ld-perf-badge { background:rgba(255,107,107,.18); color:#FF6B6B; }
.ld-perf-cohort { font-size:12px; color:var(--fm-m,#A09888); }
.ld-perf-metric-row { display:flex; align-items:center; gap:12px; margin-bottom:10px; }
.ld-perf-bar { flex:1; height:10px; background:rgba(255,255,255,.06); border-radius:5px; overflow:hidden; }
html[data-theme="light"] .ld-perf-bar { background:rgba(0,0,0,.06); }
.ld-perf-bar-fill { height:100%; background:#BFA164; border-radius:5px; transition:width .4s; }
.perf-star .ld-perf-bar-fill { background:#50C878; }
.perf-on_track .ld-perf-bar-fill { background:#4FC3F7; }
.perf-watch .ld-perf-bar-fill { background:#FF9800; }
.perf-at_risk .ld-perf-bar-fill { background:#FF6B6B; }
.ld-perf-score { font-size:14px; font-weight:700; color:var(--fm-h,#F5F0E8); min-width:60px; text-align:right; }
.ld-perf-reason { font-size:14px; color:var(--fm-h,#F5F0E8); font-weight:600; margin-bottom:8px; }
.ld-perf-footnote { font-size:11px; color:var(--fm-s,#6B6356); line-height:1.5; }

/* FEEDLOT BUY DECISION */
.ld-elig-section.elig-buy   { border-left:4px solid #50C878; background:linear-gradient(90deg,rgba(80,200,120,.06),var(--fm-bg1,#141414) 40%)!important; }
.ld-elig-section.elig-review{ border-left:4px solid #FF9800; background:linear-gradient(90deg,rgba(255,152,0,.06),var(--fm-bg1,#141414) 40%)!important; }
.ld-elig-section.elig-pass  { border-left:4px solid #FF6B6B; background:linear-gradient(90deg,rgba(255,107,107,.08),var(--fm-bg1,#141414) 40%)!important; }
.ld-elig-section.elig-unknown { border-left:4px solid #6B6356; }
.ld-elig-verdict { display:flex; align-items:center; gap:6px; flex-wrap:wrap; font-size:16px; color:var(--fm-h,#F5F0E8); margin-bottom:8px; }
.elig-buy .ld-elig-verdict   { color:#50C878; }
.elig-review .ld-elig-verdict{ color:#FF9800; }
.elig-pass .ld-elig-verdict  { color:#FF6B6B; }
.ld-elig-verdict i { font-size:22px; }
.ld-elig-ref { font-size:11px; color:var(--fm-s,#6B6356); font-weight:400; margin-left:auto; }
.ld-elig-template-note { font-size:11px; color:var(--fm-s,#6B6356); margin-bottom:10px; padding:6px 10px; background:rgba(191,161,100,.06); border:1px solid rgba(191,161,100,.18); border-radius:6px; }
.ld-elig-checks { list-style:none; padding:0; margin:0 0 14px 0; display:flex; flex-direction:column; gap:6px; }
.ld-elig-checks li { display:flex; gap:10px; align-items:flex-start; padding:8px 10px; background:var(--fm-bg2,#0F0F0F); border-radius:8px; }
html[data-theme="light"] .ld-elig-checks li { background:#F7F5F2!important; }
.ld-elig-checks li i { font-size:18px; line-height:1.2; flex-shrink:0; }
.ld-elig-checks li.pass i { color:#50C878; }
.ld-elig-checks li.fail i { color:#FF6B6B; }
.ld-elig-checks li strong { display:block; font-size:13px; color:var(--fm-h,#F5F0E8); margin-bottom:2px; }
.ld-elig-checks li span { font-size:12px; color:var(--fm-m,#A09888); }
.ld-elig-proj { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#2A2520); border-radius:10px; padding:12px; }
html[data-theme="light"] .ld-elig-proj { background:#F7F5F2!important; }
.ld-elig-proj-title { font-size:12px; font-weight:700; color:var(--fm-h,#F5F0E8); margin-bottom:10px; text-transform:uppercase; letter-spacing:.5px; }
.ld-elig-proj-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(160px, 1fr)); gap:10px; }
.ld-elig-proj-cell { display:flex; flex-direction:column; gap:2px; padding:8px 10px; background:var(--fm-bg1,#141414); border-radius:6px; }
html[data-theme="light"] .ld-elig-proj-cell { background:#FFFFFF!important; border:1px solid #E8E2DA; }
.ld-elig-proj-cell span { font-size:10px; color:var(--fm-s,#6B6356); text-transform:uppercase; letter-spacing:.4px; }
.ld-elig-proj-cell strong { font-size:14px; color:var(--fm-h,#F5F0E8); font-weight:700; }
.ld-elig-margin.positive strong { color:#50C878; }
.ld-elig-margin.negative strong { color:#FF6B6B; }
.ld-elig-margin { border:1px solid rgba(80,200,120,.25); }
.ld-elig-margin.negative { border-color:rgba(255,107,107,.3); }

/* DETAIL ITEMS */
.ld-detail-item { background:var(--fm-bg2,#0F0F0F)!important; border-radius:10px; padding:12px; }
.ld-detail-label { color:var(--fm-m,#A09888)!important; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:4px; }
.ld-detail-value { color:var(--fm-h,#F5F0E8)!important; font-size:14px; font-weight:600; }

/* DESCRIPTION */
.ld-description { color:var(--fm-m,#A09888)!important; font-size:14px; line-height:1.8; white-space:pre-line; }

/* HEALTH RECORDS */
.ld-health-list { display:flex; flex-direction:column; gap:10px; }
.ld-health-item { display:flex; align-items:center; gap:12px; background:var(--fm-bg2,#0F0F0F)!important; border-radius:10px; padding:10px 14px; }
.ld-health-icon { width:36px; height:36px; background:rgba(191,161,100,.1)!important; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

/* TAGS */
.ld-tag { background:rgba(191,161,100,.08)!important; color:var(--fm-m,#A09888)!important; padding:4px 12px; border-radius:50px; font-size:12px; border:1px solid var(--fm-br,#2A2520); }

/* ACTION CARD */
.ld-action-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:16px; padding:24px; margin-bottom:16px; }
.ld-sidebar-sticky { position:sticky; top:70px; max-height:calc(100vh - 80px); overflow-y:auto; scrollbar-width:none; }
.ld-sidebar-sticky::-webkit-scrollbar { display:none; }
.ld-price-section { margin-bottom:20px; padding-bottom:16px; border-bottom:1px solid var(--fm-br,#2A2520); }
.ld-price-big { font-size:28px; font-weight:800; color:#BFA164!important; }
.ld-price-unit { font-size:13px; font-weight:500; color:var(--fm-s,#6B6356); margin-left:6px; letter-spacing:.3px; }
.ld-bcs-pill { font-size:11px; padding:5px 10px; border-radius:20px; display:inline-flex; align-items:center; }
.ld-buy-mode-tabs { display:flex; gap:4px; border-bottom:1px solid var(--fm-br,#2A2520); padding-bottom:4px; margin-bottom:8px; }
.ld-buy-mode-tab { background:transparent; border:none; color:var(--fm-m,#A09888); font-size:12px; font-weight:600; padding:6px 12px; cursor:pointer; border-radius:6px 6px 0 0; }
.ld-buy-mode-tab.active { color:#BFA164; background:rgba(191,161,100,.08); border-bottom:2px solid #BFA164; }
.ld-escrow-badge { display:flex; align-items:center; gap:12px; background:rgba(52,195,143,.06)!important; border:1px solid rgba(52,195,143,.15); border-radius:10px; padding:12px; margin-top:16px; }

/* SELLER CARD */
.ld-seller-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:16px; padding:20px; margin-bottom:16px; }
.ld-seller-avatar-lg { width:56px; height:56px; background:#BFA164!important; color:#0A0A0A!important; border-radius:50%; font-size:20px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden; }
.ld-seller-photo { width:100%; height:100%; object-fit:cover; }
.ld-seller-stats { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:12px; }
.ld-seller-stat { background:var(--fm-bg2,#0F0F0F)!important; border-radius:8px; padding:10px; text-align:center; }
.ld-stat-value { color:var(--fm-h,#F5F0E8)!important; font-size:16px; font-weight:700; }
.ld-stat-label { color:var(--fm-m,#A09888)!important; font-size:11px; }

/* INFO CARDS */
.ld-info-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:16px; padding:20px; margin-bottom:16px; }
.ld-delivery-option { display:flex; align-items:center; gap:12px; padding:8px 0; font-size:14px; }
.ld-delivery-option i { font-size:20px; }
.ld-tips-list { padding-left:18px; margin:0; }
.ld-tips-list li { color:var(--fm-m,#A09888)!important; font-size:13px; margin-bottom:6px; }

/* RELATED LISTINGS */
.ld-related-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:14px; overflow:hidden; cursor:pointer; transition:all .3s; height:100%; }
.ld-related-card:hover { border-color:#444; transform:translateY(-3px); }
.ld-related-img { height:120px; background:linear-gradient(135deg,#1A1510,#0F0F0F); display:flex; align-items:center; justify-content:center; overflow:hidden; }
.ld-mini-avatar { width:16px; height:16px; background:#BFA164!important; color:#0A0A0A!important; border-radius:50%; font-size:7px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

/* MOBILE BOTTOM BAR */
.ld-mobile-bar { position:fixed; bottom:0; left:0; right:0; background:var(--fm-bg1,#141414)!important; border-top:1px solid var(--fm-br,#2A2520); padding:12px 16px calc(12px + env(safe-area-inset-bottom, 0px)); display:flex; justify-content:space-between; align-items:center; z-index:1040; }
.ld-mobile-price { }

/* RESPONSIVE */
@media (max-width:991px) {
  .ld-main-photo { height:300px; }
  .ld-sidebar-sticky { position:static; max-height:none; overflow:visible; }
}
@media (max-width:575px) {
  .ld-main-photo { height:220px; }
  .ld-detail-item { padding:8px; }
  .ld-related-img { height:100px; }
}
</style>
